import { isFunction } from "lodash-es";
import React, { ReactElement, ReactNode, useMemo, useState } from "react";

import { ReactHelper } from "@/common/helpers/react";
import { ValidationHelper, ValidationInfo } from "@/common/validation";

import ConfirmationModal, { ConfirmationModalProps } from "./ConfirmationModal";

type TriggerFuncType = (isModalOpened: boolean) => ReactElement;

interface Props {
  trigger: TriggerFuncType | ReactElement;
  body: ReactNode | (({ error }: { error: ValidationInfo | null }) => ReactElement);
}
export type ConfirmationModalWithTriggerProps = Props &
  Omit<ConfirmationModalProps, "open" | "onClose" | "onCancel" | "body"> & {
    onClose?: () => void;
  };

export default function ConfirmationModalWithTrigger({
  trigger,
  body,
  onConfirm,
  onClose,
  ...otherProps
}: ConfirmationModalWithTriggerProps) {
  const [isModalOpened, setIsOpened] = useState(false);
  const [confirmError, setConfirmError] = useState<ValidationInfo | null>(null);

  const handleOpen = () => {
    setIsOpened(true);
  };
  const handleClose = () => {
    setIsOpened(false);
    onClose && onClose();
  };

  const triggerElement = useMemo(() => {
    const element: ReactElement = typeof trigger === "function" ? trigger(isModalOpened) : trigger;
    return ReactHelper.cloneWithRef(element, {
      onClick: (e: React.MouseEvent<HTMLElement>) => {
        e?.stopPropagation();
        e?.preventDefault();
        if ((trigger as ReactElement).props && (trigger as ReactElement<any>).props.onClick) {
          (trigger as ReactElement<any>).props.onClick(e);
        }
        handleOpen();
      },
    });
  }, [trigger, isModalOpened, handleOpen]);

  return (
    <>
      {triggerElement}

      <ConfirmationModal
        open={isModalOpened}
        onClose={handleClose}
        onCancel={handleClose}
        onConfirm={async (params) => {
          try {
            onConfirm && (await onConfirm(params));
            handleClose();
          } catch (error) {
            setConfirmError(ValidationHelper.handleApiErrorResponse(error));
          }
        }}
        body={isFunction(body) ? body({ error: confirmError }) : body}
        {...otherProps}
      />
    </>
  );
}
